/**
 * The one canonical hash used by every NL-014 receipt slot.
 *
 * Three properties matter here, and each is load-bearing for the privacy claim
 * the receipt contract makes:
 *
 * 1. **It is really SHA-256.** Not a checksum wearing a hash's name. The tests
 *    pin the published NIST vectors, so a future "optimisation" that swaps in a
 *    non-cryptographic digest fails immediately. A receipt is a provenance
 *    record; if the digest were invertible, hashing the estimate inputs would
 *    protect nothing.
 * 2. **It is domain-separated.** Every slot hashes `"<domain>\n<canonical>"`, so
 *    structurally identical inputs and outputs cannot collide, and a digest
 *    lifted from one slot cannot be replayed into another.
 * 3. **It is deterministic.** Object keys are sorted before serialization, so
 *    the same logical value always produces the same digest regardless of
 *    property insertion order.
 *
 * The implementation is pure, synchronous and dependency-free: no clock, no
 * randomness, no network, no environment access, and no `crypto.subtle` (which
 * is async and unavailable on the server render path). That keeps it usable
 * from the same synchronous contract path as the rest of the NL-014 modules.
 */

export const CANONICAL_HASH_ALGORITHM = "sha256" as const;

/**
 * The only shape a receipt hash slot may hold: the algorithm label, a colon,
 * and exactly 64 lowercase hex characters.
 *
 * This is a *positive* format check by design. The previous guard only rejected
 * things that were obviously not hashes (bare numbers, whitespace, `@`), which
 * let `"+972-50-1234567"`, `"https://crm.example/lead/44213"` and `"1e5"`
 * through. Anchoring on the exact digest shape means a slot either holds a
 * digest of the right algorithm and width or it fails closed.
 */
export const CANONICAL_HASH_PATTERN = /^sha256:[0-9a-f]{64}$/;

/** Domain tags. One per hashed slot; never reuse a tag across slots. */
export const CANONICAL_HASH_DOMAINS = {
  receiptInputs: "nl014:receipt:inputs:v1",
  receiptOutputs: "nl014:receipt:outputs:v1",
  receiptResult: "nl014:receipt:result:v1",
} as const;

export type CanonicalHashDomain =
  (typeof CANONICAL_HASH_DOMAINS)[keyof typeof CANONICAL_HASH_DOMAINS];

/**
 * The value shapes this hash accepts.
 *
 * Deliberately narrow. A `Date`, `Map`, `Set` or class instance has no stable
 * canonical form here and would serialize as `{}` — meaning two different values
 * would produce the same digest, which is the one thing a hash must never do.
 * Restricting the input at the type level rules that out at compile time
 * instead of leaving it to a runtime check nobody runs.
 */
export type CanonicalValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly CanonicalValue[]
  | { readonly [key: string]: CanonicalValue };

/** Stable stringification with lexicographically sorted object keys. */
export function canonicalize(value: CanonicalValue): string {
  // `undefined` and `null` are both "absent"; JSON.stringify returns undefined
  // for the former, so it is normalized here rather than leaking a non-string.
  if (value === undefined) return "null";
  if (value === null || typeof value !== "object") {
    // A non-finite number also stringifies to "null"; that is JSON's behaviour
    // and is acceptable because the callers hash schema-bounded finite values.
    return JSON.stringify(value) ?? "null";
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry) => canonicalize(entry)).join(",")}]`;
  }

  const record = value as { readonly [key: string]: CanonicalValue };
  const keys = Object.keys(record).sort();
  return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalize(record[key])}`).join(",")}}`;
}

/**
 * UTF-8 bytes of `text`, matching WHATWG encoding (and therefore `TextEncoder`
 * and node's `Buffer.from(s, "utf8")`) exactly.
 *
 * Unpaired surrogates become U+FFFD rather than CESU-8, so this agrees with the
 * platform encoders on every possible JavaScript string, not merely on
 * well-formed ones. That matters because two strings differing only in an
 * unpaired surrogate must not collide, and because `sha256Hex` is exported.
 */
function utf8Bytes(text: string): number[] {
  const bytes: number[] = [];
  const pushReplacement = () => bytes.push(0xef, 0xbf, 0xbd);

  for (let index = 0; index < text.length; index += 1) {
    let code = text.charCodeAt(index);

    if (code < 0x80) {
      bytes.push(code);
      continue;
    }
    if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
      continue;
    }

    // High surrogate: valid only when followed by a low surrogate.
    if (code >= 0xd800 && code <= 0xdbff) {
      const low = index + 1 < text.length ? text.charCodeAt(index + 1) : Number.NaN;

      if (low >= 0xdc00 && low <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (low - 0xdc00);
        bytes.push(
          0xf0 | (code >> 18),
          0x80 | ((code >> 12) & 0x3f),
          0x80 | ((code >> 6) & 0x3f),
          0x80 | (code & 0x3f),
        );
        index += 1;
        continue;
      }

      pushReplacement();
      continue;
    }

    // A low surrogate reached here has no preceding high surrogate.
    if (code >= 0xdc00 && code <= 0xdfff) {
      pushReplacement();
      continue;
    }

    bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
  }

  return bytes;
}

/** FIPS 180-4 round constants: the first 32 bits of the fractional parts of the cube roots of the first 64 primes. */
const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
] as const;

function rotr(value: number, shift: number): number {
  return (value >>> shift) | (value << (32 - shift));
}

/**
 * SHA-256 of a UTF-8 string, as 64 lowercase hex characters.
 *
 * Straight FIPS 180-4 with 32-bit arithmetic, so it needs no BigInt and stays
 * within the project's ES2017 target. `Math.imul` is not required because the
 * compression function uses only additions, shifts and xors.
 */
export function sha256Hex(text: string): string {
  const bytes = utf8Bytes(text);
  const bitLength = bytes.length * 8;

  // Padding: 0x80, then zeros, then the 64-bit big-endian length.
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  // The high 32 bits of the length: safe as 0 for any input this project hashes
  // (it would need a 512 MB string to overflow), but computed rather than assumed.
  const highLength = Math.floor(bitLength / 0x100000000);
  bytes.push(
    (highLength >>> 24) & 0xff,
    (highLength >>> 16) & 0xff,
    (highLength >>> 8) & 0xff,
    highLength & 0xff,
    (bitLength >>> 24) & 0xff,
    (bitLength >>> 16) & 0xff,
    (bitLength >>> 8) & 0xff,
    bitLength & 0xff,
  );

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const w = new Array<number>(64);

  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) {
      const j = offset + i * 4;
      w[i] = ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) >>> 0;
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;

    for (let i = 0; i < 64; i += 1) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + ch + SHA256_K[i] + w[i]) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }

  const hex = (n: number) => n.toString(16).padStart(8, "0");
  return `${hex(h0)}${hex(h1)}${hex(h2)}${hex(h3)}${hex(h4)}${hex(h5)}${hex(h6)}${hex(h7)}`;
}

/**
 * The canonical, domain-separated digest of `value` for one receipt slot.
 *
 * Returns `sha256:<64 hex>` — the only shape `CANONICAL_HASH_PATTERN` accepts.
 */
export function canonicalHash(domain: CanonicalHashDomain, value: CanonicalValue): string {
  return `${CANONICAL_HASH_ALGORITHM}:${sha256Hex(`${domain}\n${canonicalize(value)}`)}`;
}

/** True when `value` is a well-formed canonical hash. */
export function isCanonicalHash(value: unknown): value is string {
  return typeof value === "string" && CANONICAL_HASH_PATTERN.test(value);
}
